import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Trash2, Eye, Slack, GitBranch, FileText, Folder, AlertCircle, Workflow } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export default function ProjectWatchpointsTab({ project, onUpdate }) {
  const [watchpoints, setWatchpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [watchpointToDelete, setWatchpointToDelete] = useState(null);

  useEffect(() => {
    loadWatchpoints();
  }, [project.id]);

  const loadWatchpoints = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${project.id}/watchpoints`);
      setWatchpoints(response.data || []);
    } catch (error) {
      console.error('Error loading watchpoints:', error);
      toast.error('Failed to load watchpoints');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (watchpoint) => {
    setWatchpointToDelete(watchpoint);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!watchpointToDelete) return;

    try {
      await api.delete(`/projects/${project.id}/watchpoints/${watchpointToDelete.id}`);
      setWatchpoints(watchpoints.filter(wp => wp.id !== watchpointToDelete.id));
      toast.success('Watchpoint removed');
      if (onUpdate) onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to remove watchpoint';
      toast.error(errorMsg);
    } finally {
      setDeleteDialogOpen(false);
      setWatchpointToDelete(null);
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'slack_channels':
        return Slack;
      case 'github_repos':
        return GitBranch;
      case 'google_docs':
        return FileText;
      case 'google_drive_folders':
        return Folder;
      case 'linear_teams':
        return Workflow;
      default:
        return AlertCircle;
    }
  };

  const getResourceTypeLabel = (type) => {
    switch (type) {
      case 'slack_channels':
        return 'Slack Channel';
      case 'github_repos':
        return 'GitHub Repo';
      case 'google_docs':
        return 'Google Doc';
      case 'google_drive_folders':
        return 'Google Drive Folder';
      case 'linear_teams':
        return 'Linear Team';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-48 w-full bg-muted animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Watchpoints</h2>
          <p className="text-muted-foreground mt-1">
            Resources being monitored for this project. Watchpoints are automatically created when you add sources.
          </p>
        </div>
      </div>

      {watchpoints.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <Eye className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No watchpoints yet</h3>
            <p className="text-muted-foreground mb-4">
              Watchpoints are automatically created when you connect sources to this project.
            </p>
            <p className="text-sm text-muted-foreground">
              Go to the <strong>Sources</strong> tab to connect Slack channels, GitHub repos, or Google Drive folders.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {watchpoints.map((watchpoint) => (
            <Card key={watchpoint.id} className="hover:bg-muted/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{watchpoint.name}</CardTitle>
                    <CardDescription className="mt-1">
                      Created {formatDate(watchpoint.created_at)}
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteClick(watchpoint)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {watchpoint.resources && watchpoint.resources.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">Watching:</p>
                    <div className="flex flex-wrap gap-2">
                      {watchpoint.resources.map((resource, idx) => {
                        const Icon = getResourceIcon(resource.type);
                        return (
                          <Badge key={idx} variant="outline" className="flex items-center gap-1.5">
                            <Icon className="h-3 w-3" />
                            <span>{getResourceTypeLabel(resource.type)}</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="font-medium">{resource.name}</span>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No resources linked yet</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Watchpoint?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the watchpoint "{watchpointToDelete?.name}"? 
              This will stop monitoring the associated resources. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

